// "ƒx Examples" — a Liquid cheatsheet for table cells and free text. Every
// snippet is click-to-insert (into the table cell the caller resolves) and
// click-to-copy. Examples are personalised from the imported dataset when one
// is present (real field names, first numeric column for the sum examples).
import { el, openModal, toast } from './../ui/dom'
import { state } from './../state'

type Insert = (tpl: string) => boolean

interface Snippet { code: string; desc: string }
interface Section { title: string; note?: string; snippets: Snippet[] }

/** First imported column whose first value parses as a number (else fallback). */
function numericField(fallback = 'amount'): string {
  for (const h of state.batchHeaders ?? []) {
    const v = state.batchData?.[0]?.[h]
    if (v !== '' && v != null && !Number.isNaN(Number(v))) return h
  }
  return fallback
}

function buildSections(): Section[] {
  const headers = state.batchHeaders ?? []
  const f = headers[0] ?? 'name'
  const num = numericField()
  const cents = headers.find((h) => /cent/i.test(h))
  return [
    {
      title: 'Fields',
      note: 'A field prints the value of that column — in a Data Table template row it repeats per record.',
      snippets: [
        { code: `{{${f}}}`, desc: 'value of a column' },
        { code: `{{ ${num} | number: 2 }}`, desc: 'number with 2 decimals' },
        { code: `{{ ${num} | money: "EUR", "nl-NL" }}`, desc: 'currency (€ 1.234,56)' },
        ...(cents ? [{ code: `{{ ${cents} | divided_by: 100.0 | money: "EUR", "nl-NL" }}`, desc: 'cents → euros' }] : []),
        { code: `{{ ${headers.find((h) => /date|_at$/i.test(h)) ?? 'date'} | date: "%d %b %Y, %H:%M" }}`, desc: 'format a date/time' },
      ],
    },
    {
      title: 'Summaries — over the whole dataset',
      note: 'For summary tables and the rows under a Data Table. `rows` is your imported dataset.',
      snippets: [
        { code: '{{ rows | count }}', desc: 'number of rows' },
        { code: `{{ rows | sum: "${num}" }}`, desc: 'sum of a column' },
        { code: `{{ rows | sum: "${cents ?? num}" ${cents ? '| divided_by: 100.0 ' : ''}| money: "EUR", "nl-NL" }}`, desc: 'total as currency' },
        { code: `{{ rows | avg: "${num}" | number: 2 }}`, desc: 'average' },
        { code: `{{ rows | where: "type", "call" | sum: "${num}" }}`, desc: 'subtotal of matching rows only' },
        { code: `{{ rows | max: "${num}" }}`, desc: 'highest value (also: min)' },
      ],
    },
    {
      title: 'Math',
      note: 'Chain after a field or a summary.',
      snippets: [
        { code: '| divided_by: 100.0', desc: 'divide (use 100.0 for decimals)' },
        { code: '| times: 1.21', desc: 'multiply (e.g. add 21% VAT)' },
        { code: '| plus: 5', desc: 'add (also: minus)' },
        { code: '| ceil', desc: 'round up (also: floor, round)' },
      ],
    },
  ]
}

export function openLiquidHelp(insert?: Insert): void {
  const body = el('div', 'flex flex-col gap-4')
  body.append(el('p', 'text-xs text-slate-400 -mt-1',
    'Cells and text can compute values with these expressions. Click a snippet to copy it' +
    (insert ? ', or use Insert to write it into the selected table cell.' : '.')))

  for (const sec of buildSections()) {
    const wrap = el('div')
    wrap.append(el('div', 'text-[11px] font-semibold uppercase tracking-wider text-slate-400 mb-1', sec.title))
    if (sec.note) wrap.append(el('div', 'text-[11px] text-slate-500 mb-2', sec.note))
    const list = el('div', 'flex flex-col gap-1')
    for (const s of sec.snippets) {
      const rowEl = el('div', 'flex items-center gap-2 rounded-lg border border-slate-700 bg-slate-800/60 px-3 py-1.5')
      const code = el('code', 'text-[11px] text-blue-400 cursor-pointer flex-1 truncate', s.code)
      code.title = 'Click to copy'
      code.addEventListener('click', () => {
        void navigator.clipboard?.writeText(s.code)
        toast('Copied')
      })
      const desc = el('span', 'text-[11px] text-slate-500 shrink-0', s.desc)
      rowEl.append(code, desc)
      if (insert) {
        const btn = el('button', 'tb-btn text-[11px] shrink-0', 'Insert') as HTMLButtonElement
        btn.type = 'button'
        btn.style.cssText = 'background:#1e293b;border:1px solid #334155;padding:2px 10px'
        btn.addEventListener('click', () => {
          if (insert(s.code)) toast('Inserted into the selected cell')
          else toast('Click a cell in the table first')
        })
        rowEl.append(btn)
      }
      list.append(rowEl)
    }
    wrap.append(list)
    body.append(wrap)
  }

  if (!state.batchHeaders?.length) {
    body.append(el('div', 'text-[11px] text-slate-500',
      '💡 Import Data first and these examples will use your real column names.'))
  }

  openModal({ title: 'ƒx Expression examples', body, width: '640px', testid: 'liquid-help' })
}
